import { AlternativeIncomeModel, CaseData, MedicalCoverageType, PartyModel, PensionContributionModel, SpousalSupportModel } from "@/types/case";
import { FederalIncomeModel, FederalIncomeModelDebug, NetMonthlyIncomeModel, OtherDeductionsModel, SocialSecurityPensionDeductionModel, SourcesAndAmountsModel, StateIncomeModel, TotalsModel, Worksheet1PartyModel, Worksheet1Section3Model, Worksheet1Section4Model, Worksheet1Section5Model, Worksheet1Section6Model } from "@/calcs/documents/worksheet1";
import { avgBy, convertMedicalCoverageTypeToLabel, getYearInfo, getPetitionerParty, getRespondentParty, getSchoolDistrictSurtaxRate, isCustodialParent, isNoncustodialParent, isSharedParent, isSplitParent, roundTo, sumBy } from "@/calcs/utils";
import { CashMedicalSupportLevel, ChildSupportLevel, FilingStatusKey, TaxRules } from "@/calcs/types";

import { childSupportLevelsIA2025 as childSupportLevels } from "./childSupportLevels";
import { cashMedicalSupportLevelsIA2025 as cashMedicalSupportLevels } from "./cashMedicalSupportLevels";
import { taxRules2025 as taxRules } from "../taxRules";
import { stateDependentCareIA2025 as stateDependentCareLevels } from "./stateDependentCare";
import { debugLog } from "@/calcs/calcDebug";


function getChildSupportLevel(incomeAmount: number): ChildSupportLevel {
    const levels: ChildSupportLevel[] = childSupportLevels;

    if ((levels ?? []).length === 0 || incomeAmount < 0.0) {
        const rv: ChildSupportLevel = {
            incomeArea: 'a',
            amounts: [0.0, 0.0, 0.0, 0.0, 0.0],
            lowIncomes: [false, false, false, false, false],
            combinedAdjustedNetIncomeMinimum: 0,
            combinedAdjustedNetIncomeMaximum: 0,
        };
        return rv;
    } else {
        const level = levels.find(x => x.combinedAdjustedNetIncomeMinimum <= incomeAmount && x.combinedAdjustedNetIncomeMaximum >= incomeAmount) ??
            levels[levels.length - 1];
        const rv: ChildSupportLevel = {
            incomeArea: level.incomeArea,
            amounts: level.amounts,
            lowIncomes: level.lowIncomes,
            combinedAdjustedNetIncomeMinimum: level.combinedAdjustedNetIncomeMinimum,
            combinedAdjustedNetIncomeMaximum: level.combinedAdjustedNetIncomeMaximum,
        };
        return rv;
    }
}


export function getCashMedicalSupportLevel(incomeAmount: number): CashMedicalSupportLevel | null {
    const levels: CashMedicalSupportLevel[] = cashMedicalSupportLevels;

    if ((levels ?? []).length === 0 || incomeAmount < 0.0) {
        return null;
    } else {
        return levels.find(x => x.minimumIncomeAmount <= incomeAmount && x.maximumIncomeAmount >= incomeAmount) ??
            levels[levels.length - 1];
    }

}



function calcNetMonthlyIncome(caseData: CaseData, oddIfAlternating: boolean, party: PartyModel | null): NetMonthlyIncomeModel {
    const yearInfo = getYearInfo(caseData.children.taxSchedule ?? 'fixed', oddIfAlternating, party);
    const children0to16 = ((yearInfo?.age5Under ?? 0) + (yearInfo?.age6to16 ?? 0));
    const children17Plus = yearInfo?.age17Over ?? 0;
    const childrenCount = caseData.children.count ?? 0;
    return {
        isCustodialParent: isCustodialParent(caseData, party),
        isNoncustodialParent: isNoncustodialParent(caseData, party),
        isShared: isSharedParent(caseData),
        isSplit: isSplitParent(caseData),
        children0to16: children0to16,
        children17Plus: children17Plus,
        childrenCount: childrenCount
    };

}

function calcSourcesAndAmounts(caseData: CaseData, party: PartyModel | null, netMonthlyIncome: NetMonthlyIncomeModel): SourcesAndAmountsModel {
    const wages = (party?.income?.primary?.yearlyWages ?? 0.0);
    const selfEmployment = (party?.income?.primary?.yearlySelfEmployment ?? 0.0);
    const otherIncomes = sumBy<AlternativeIncomeModel>(party?.income?.others ?? [], x => x?.amount ?? 0.0);
    const spousalSupportReceivedYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.otherParty === 'party' && x?.payOrReceive === "receive" ? (x?.amount ?? 0.0) : 0.0) * 12.0;
    const spousalSupportPaidYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.otherParty === 'party' && x?.payOrReceive === "pay" ? (x?.amount ?? 0.0) : 0.0) * 12.0;
    const thirdPartyAlimonyReceivedYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.otherParty === 'thirdParty' && x?.payOrReceive === "receive" ? (x?.amount ?? 0.0) : 0.0) * 12.0;
    const thirdPartyAlimonyPaidYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.otherParty === 'thirdParty' && x?.payOrReceive === "pay" ? (x?.amount ?? 0.0) : 0.0) * 12.0;

    const thirdPartyReimbursements = party?.deductions?.childCareReimbursements ?? 0.0;
    const pensionDeductions = (sumBy<PensionContributionModel>(party?.pensions ?? [], x => x?.isIncludedInGrossWages === false ? (x?.computedAmount ?? (x?.income ?? 0.0) * (x?.rate ?? 0.0)) : 0.0));

    const sourcesAndAmounts = wages + selfEmployment + otherIncomes + Math.abs(pensionDeductions) + thirdPartyReimbursements + Math.abs(thirdPartyAlimonyReceivedYearly) - Math.abs(thirdPartyAlimonyPaidYearly);
    const total = sourcesAndAmounts + spousalSupportReceivedYearly - Math.abs(spousalSupportPaidYearly);

    return {
        sourcesAndAmountsOfAnnualIncome: sourcesAndAmounts,
        lessSpousalSupport: spousalSupportPaidYearly,
        plusSpousalSupport: spousalSupportReceivedYearly,
        total: total,
        debug: {
            wages: wages,
            selfEmployment: selfEmployment,
            otherIncomes: otherIncomes,
            thirdPartyReimbursements: thirdPartyReimbursements,
            spousalSupportPaidYearly: spousalSupportPaidYearly,
            spousalSupportReceivedYearly: spousalSupportReceivedYearly
        }
    };
}

function calcQbiDeduction(party: PartyModel | null, selfEmployment: number, grossAnnualIncome: number, halfFicaTax: number, standardDeduction: number): number {

    const isQbi = party?.income?.primary?.isQBI ?? false;
    if (!isQbi) {
        return 0.0;
    }

    const tentativeRate = taxRules.federal.qbi.tentativeRate;
    const thresholdStart = taxRules.federal.qbi.thresholdStart;
    const thresholdEnd = taxRules.federal.qbi.thresholdEnd;
    const thresholdRange = thresholdEnd - thresholdStart;

    const qbiWages = Math.abs(party?.income?.primary?.qbiYearlyW2Wages ?? 0.0);
    const qbiAssets = Math.abs(party?.income?.primary?.qbiYearlyFixedAssets ?? 0.0);
    const isQualifiedTrade = party?.income?.primary?.isQualifiedTrade ?? false;

    const tentativeAmount = roundTo(tentativeRate * selfEmployment, 2);

    // we don't remove personal exemptions here becasue as of 2025, those are always $0.00
    const totalTaxableIncome = roundTo(grossAnnualIncome - Math.abs(halfFicaTax) - Math.abs(standardDeduction), 2);

    const limitAmount = roundTo(Math.max(0.5 * qbiWages, (0.25 * qbiWages) + (0.025 * qbiAssets)), 2);

    const belowRange = totalTaxableIncome <= thresholdStart;
    const aboveRange = totalTaxableIncome > thresholdEnd;


    if (belowRange) {
        // income is below the bottom of the threshold range. just use the 20% of gross annual income.
        return roundTo(-1.0 * tentativeAmount, 2);
    }

    if (aboveRange) {
        if (isQualifiedTrade) {
            return 0.0;
        } else {
            return roundTo(-1.0 * Math.min(tentativeAmount, limitAmount), 2);
        }
    }

    // we get here, we are within the threshold range.

    const phaseInRate = roundTo((totalTaxableIncome - thresholdStart) / thresholdRange, 4);

    if (isQualifiedTrade) {
        // This IS a qualified service / trade / business.
        const allowedFraction = roundTo(1.0 - phaseInRate, 4);

        return roundTo(-1.0 * (tentativeAmount * allowedFraction), 2);

    } else {
        // This is NOT a qualified services / trade business.

        // income is within threshold range (above the bottom and below the top)
        const excess = roundTo(Math.max(tentativeAmount - Math.abs(limitAmount), 0.0), 2);

        const reduction = roundTo(excess * phaseInRate, 2);

        return roundTo(-1.0 * (tentativeAmount - reduction), 2);

    }


}

function calcFederalTaxDeduction(caseData: CaseData, oddIfAlternating: boolean, party: PartyModel | null, sourcesAndAmounts: SourcesAndAmountsModel, taxRules: TaxRules): FederalIncomeModel {

    const debug: FederalIncomeModelDebug = {};

    const wages = (party?.income?.primary?.yearlyWages ?? 0.0);
    const selfEmployment = (party?.income?.primary?.yearlySelfEmployment ?? 0.0);
    const otherIncomes = sumBy<AlternativeIncomeModel>(party?.income?.others ?? [], x => x?.isFederalTax === true ? (x?.amount ?? 0.0) : 0.0);
    const spousalSupportReceivedYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.isEntered2019OrLater === false && x?.otherParty === 'party' && x?.payOrReceive === "receive" ? (x?.amount ?? 0.0) : 0.0) * 12.0;
    const spousalSupportPaidYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.isEntered2019OrLater === false && x?.otherParty === 'party' && x?.payOrReceive === "pay" ? (x?.amount ?? 0.0) : 0.0) * 12.0;
    const pensionDeductions = (sumBy<PensionContributionModel>(party?.pensions ?? [], x => x?.isIncludedInGrossWages === true ? (x?.computedAmount ?? (x?.income ?? 0.0) * (x?.rate ?? 0.0)) : 0.0));


    const grossTaxableIncome = roundTo(wages + selfEmployment + otherIncomes + spousalSupportReceivedYearly - Math.abs(pensionDeductions) - Math.abs(spousalSupportPaidYearly), 2);

    const halfFicaTax = roundTo(-1.0 * (0.5 * (0.153 * 0.9235 * selfEmployment)), 2);


    const yearInfo = getYearInfo(caseData.children.taxSchedule ?? '', oddIfAlternating, party);
    // Normalize the status to ensure it's a valid key, filtering out 'unknown' and null
    const rawStatus = (yearInfo?.status ?? 'single') as string;
    // Ensure we have a valid filing status key (normalize old snake_case values)
    const validStatus = (rawStatus === 'single' || rawStatus === 'marriedFilingSeparately' || rawStatus === 'headOfHousehold')
        ? rawStatus
        : (rawStatus === 'married_filing_separately' ? 'marriedFilingSeparately'
            : rawStatus === 'head_of_household' ? 'headOfHousehold'
                : 'single');
    const filingRules = taxRules.federal.filing[validStatus as FilingStatusKey] ?? taxRules.federal.filing['single'];

    const isOver65 = party?.isOver65 ?? party?.personal?.isOver65 ?? false;
    const isBlind = party?.isBlind ?? party?.personal?.isBlind ?? false;

    const children0to16 = (yearInfo?.age5Under ?? 0) + (yearInfo?.age6to16 ?? 0);
    const children17Plus = yearInfo?.age17Over ?? 0;




    const personalExemptions = 0.0; // always $0.00 as of 2025

    const over65Deduction = roundTo(-1.0 * (isOver65 ? (filingRules?.over65Deduction ?? 0.0) : 0.0), 2);
    const blindDeduction = roundTo(-1.0 * (isBlind ? (filingRules?.blindDeduction ?? 0.0) : 0.0), 2);
    const baseStandardDeduction = roundTo(-1.0 * (filingRules?.standardDeduction ?? 0.0), 2);
    const standardDeduction = roundTo(-1.0 * (Math.abs(baseStandardDeduction) + Math.abs(over65Deduction) + Math.abs(blindDeduction)), 2);

    const federalQbiDeduction = calcQbiDeduction(party, selfEmployment, grossTaxableIncome, halfFicaTax, standardDeduction);

    const netTaxableIncome = roundTo(grossTaxableIncome - Math.abs(standardDeduction) - Math.abs(halfFicaTax) - Math.abs(federalQbiDeduction), 2);

    let taxLiability = 0.0;
    for (var i = 0; i < filingRules.brackets.length; i++) {
        const bracket = filingRules.brackets[i];
        const maxIncome = bracket.maximumIncome ?? 999_999_999_999.99; // top end bracket is undefined, so we use a very large number
        if (bracket.minimumIncome <= netTaxableIncome && netTaxableIncome <= maxIncome) {
            // net income is in this bracket, so we add the amount of income in the bracket to the tax liability and jump out of the loop
            const bracketIncome = (netTaxableIncome - bracket.minimumIncome);
            taxLiability += bracketIncome * bracket.rate;
            // no need to keep going to the next bracket, we've found the correct bracket
            break;
        } else if (maxIncome <= netTaxableIncome) {
            // net income is greater than the maximum income for this bracket, so we add the entire bracket to the tax liability
            const bracketIncome = maxIncome - (bracket.minimumIncome ?? 0.0);
            taxLiability += bracketIncome * bracket.rate;
            // keep going to the next bracket
        } else {
            // net income is less than the minimum income for this bracket, so we don't add anything to the tax liability
            // should next get here since every bracket has a minimum income of 0.0.
            break;
        }
    }
    if (taxLiability < 0.0) {
        taxLiability = 0.0;
    }

    taxLiability = roundTo(-1.0 * taxLiability, 2);



    const creditPer0to16 = taxRules.federal.credits.federalCreditPerDependentUnder17 ?? 0.0;
    const creditPer17Plus = taxRules.federal.credits.federalCreditPerDependent17Over ?? 0.0;
    const refundableAmountPerChildUnder17 = taxRules.federal.credits.federalRefundableAmountPerChildUnder17 ?? 0.0;
    const childTaxCreditAmount = roundTo((children0to16 * creditPer0to16) + (children17Plus * creditPer17Plus), 2);




    let agiPhaseout = 0.0;
    if (grossTaxableIncome > taxRules.federal.credits.childTaxCreditPhaseOutThreshold) {
        const incomeOverThreshold = grossTaxableIncome - (taxRules.federal.credits.childTaxCreditPhaseOutThreshold ?? 0.0);
        const agiFactor = Math.ceil(incomeOverThreshold / (taxRules.federal.credits.dollarAbovePhaseoutThresholdIncrement ?? 1000.0));
        agiPhaseout = roundTo(-1.0 * Math.min(Math.abs(childTaxCreditAmount), Math.abs(agiFactor) * (taxRules.federal.credits.phaseOutAmountPerChild ?? 50.0)), 2);

        debug.incomeOverThreshold = incomeOverThreshold;
        debug.agiFactor = agiFactor;
    }

    let childTaxCreditRefundable = 0.0;

    let finalTaxLiability = 0.0;

    if (Math.abs(childTaxCreditAmount) > Math.abs(taxLiability)) {

        let maxRefundableAmount = roundTo(children0to16 * refundableAmountPerChildUnder17, 2);
        if (maxRefundableAmount >= (childTaxCreditAmount - Math.abs(taxLiability))) {
            maxRefundableAmount = roundTo(childTaxCreditAmount - Math.abs(taxLiability), 2);
        }
        debug.maxRefundableAmount = maxRefundableAmount;

        // earned income multiplier
        const earnedIncomeMultiplier = ((taxRules.federal.earnedIncome.agiMultiplier ?? 15.0) / 100.0);
        const earnedIncomeThreshold = roundTo(taxRules.federal.earnedIncome.threshold ?? 2500.0, 2);

        let earnedIncomeAmount = roundTo(Math.max(0.0, sourcesAndAmounts.total - earnedIncomeThreshold) * earnedIncomeMultiplier, 2);

        childTaxCreditRefundable = roundTo(Math.min(maxRefundableAmount, earnedIncomeAmount), 2);

        finalTaxLiability = roundTo(childTaxCreditRefundable + agiPhaseout, 2);

        debug.earnedIncomeAmount = earnedIncomeAmount;
        debug.earnedIncomeMultiplier = earnedIncomeMultiplier;
        debug.earnedIncomeLimit = earnedIncomeThreshold;


        // let maxRefundableAmount = roundTo(children0to16 * refundableAmountPerChildUnder17, 2);
        // if (maxRefundableAmount >= (childTaxCreditAmount - Math.abs(taxLiability))) {
        //     maxRefundableAmount = roundTo(childTaxCreditAmount - Math.abs(taxLiability), 2);
        // }
        // debug.maxRefundableAmount = maxRefundableAmount;

        // // earned income multiplier
        // const earnedIncomeMultiplier = ((taxRules.federal.earnedIncome.agiMultiplier ?? 0.15) / 100.0);
        // const earnedIncomeAmount = roundTo(taxRules.federal.earnedIncome.threshold ?? 2500.0, 2);

        // let earnedIncomeLimit = roundTo(sourcesAndAmounts.total * earnedIncomeMultiplier, 2);
        // if (sourcesAndAmounts.total > earnedIncomeAmount) {
        //     earnedIncomeLimit = roundTo((sourcesAndAmounts.total - earnedIncomeAmount) * earnedIncomeMultiplier, 2);
        // }

        // childTaxCreditRefundable = roundTo((maxRefundableAmount < earnedIncomeLimit) ? maxRefundableAmount : earnedIncomeLimit, 2);

        // finalTaxLiability = roundTo(childTaxCreditRefundable + agiPhaseout, 2);

        // debug.earnedIncomeAmount = earnedIncomeAmount;
        // debug.earnedIncomeMultiplier = earnedIncomeMultiplier;
        // debug.earnedIncomeLimit = earnedIncomeLimit;

    } else {
        // child tax credit <= taxliability
        finalTaxLiability = roundTo(taxLiability + childTaxCreditAmount + agiPhaseout, 2);
        childTaxCreditRefundable = 0.0;
    }



    return {
        grossTaxableIncome: grossTaxableIncome,
        lessHalfFicaTax: halfFicaTax,
        lessPersonalExemptions: personalExemptions,
        lessQBIDeduction: federalQbiDeduction,
        netTaxableIncome: netTaxableIncome,
        taxLiabilty: taxLiability,
        taxCreditForDependentChildren: childTaxCreditAmount,
        childTaxCreditAGIPhaseout: agiPhaseout,
        childTaxCreditRefundable: childTaxCreditRefundable,
        finalTaxLiability: finalTaxLiability,
        debug: {
            wages: wages,
            selfEmployment: selfEmployment,
            otherIncomes: otherIncomes,
            pensionDeductions: pensionDeductions,
            spousalSupportReceivedYearly: spousalSupportReceivedYearly,
            spousalSupportPaidYearly: spousalSupportPaidYearly,
            halfFicaTax: halfFicaTax,
            grossAnnualIncome: sourcesAndAmounts.total,

            incomeOverThreshold: debug.incomeOverThreshold,
            agiFactor: debug.agiFactor,
            maxRefundableAmount: debug.maxRefundableAmount,

            earnedIncomeMultiplier: debug.earnedIncomeMultiplier,
            earnedIncomeLimit: debug.earnedIncomeLimit,
            earnedIncomeAmount: debug.earnedIncomeAmount,

            over65Deduction: over65Deduction,
            blindDeduction: blindDeduction,
            baseStandardDeduction: baseStandardDeduction,
            standardDeduction: standardDeduction,
            creditPer0to16: debug.creditPer0to16,
            creditPer17Plus: debug.creditPer17Plus,
            refundableAmountPerChildUnder17: debug.refundableAmountPerChildUnder17,
            childTaxCreditAmount: debug.childTaxCreditAmount ?? childTaxCreditAmount,
        }
    };
}

function calcStateTaxDeduction(caseData: CaseData, oddIfAlternating: boolean, party: PartyModel | null, federalTaxDeduction: FederalIncomeModel, taxRules: TaxRules): StateIncomeModel {

    const stateTaxRules = taxRules.states[caseData.basic.stateCode ?? 'IA'];
    const yearInfo = getYearInfo(caseData.children.taxSchedule ?? '', oddIfAlternating, party);
    const filingStatus = yearInfo?.status ?? 'single'; // Default to 'single' if missing (validation should catch this)

    const isOver65 = party?.isOver65 ?? party?.personal?.isOver65 ?? false;
    const isBlind = party?.isBlind ?? party?.personal?.isBlind ?? false;

    const wages = (party?.income?.primary?.yearlyWages ?? 0.0);
    const selfEmployment = (party?.income?.primary?.yearlySelfEmployment ?? 0.0);
    const otherIncomes = sumBy<AlternativeIncomeModel>(party?.income?.others ?? [], x => x?.isStateTax === true ? (x?.amount ?? 0.0) : 0.0);
    const spousalSupportReceivedYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.isEntered2019OrLater === false && x?.otherParty === 'party' && x?.payOrReceive === "receive" ? (x?.amount ?? 0.0) : 0.0) * 12.0;
    const spousalSupportPaidYearly = sumBy<SpousalSupportModel>(party?.spousalSupports ?? [], x => x?.isEntered2019OrLater === false && x?.otherParty === 'party' && x?.payOrReceive === "pay" ? (x?.amount ?? 0.0) : 0.0) * 12.0;
    const pensionDeductions = (sumBy<PensionContributionModel>(party?.pensions ?? [], x => x?.isIncludedInGrossWages === true ? (x?.computedAmount ?? (x?.income ?? 0.0) * (x?.rate ?? 0.0)) : 0.0));

    // now we've gathered raw inputs. start calculations.

    const totalDependentsClaimed = (yearInfo?.age5Under ?? 0) + (yearInfo?.age6to16 ?? 0) + (yearInfo?.age17Over ?? 0);


    const over65Deduction = roundTo(isOver65 ? (stateTaxRules?.over65Deduction ?? 0.0) : 0.0, 2);
    const blindDeduction = roundTo(isBlind ? (stateTaxRules?.blindDeduction ?? 0.0) : 0.0, 2);


    const grossIncome = roundTo(wages + selfEmployment + otherIncomes + spousalSupportReceivedYearly - Math.abs(pensionDeductions) - Math.abs(spousalSupportPaidYearly), 2);

    const halfFicaTax = roundTo(-1.0 * (0.5 * (0.153 * 0.9235 * selfEmployment)), 2);

    const federalQBIDeduction = (party?.income?.primary?.isQBI ?? false) ? roundTo(-1.0 * (0.5 * Math.abs(federalTaxDeduction.lessQBIDeduction)), 2) : 0.0;

    // note: net taxable income is the same as the federal taxable income as of 2025
    const netTaxableIncome = federalTaxDeduction.netTaxableIncome; // roundTo(grossIncome - (0.5 * selfEmployment) - federalQBIDeduction, 2);

    let taxLiability = 0.0;
    for (var i = 0; i < stateTaxRules.brackets.length; i++) {
        const bracket = stateTaxRules.brackets[i];
        const maxIncome = bracket.maximumIncome ?? 999_999_999_999.99; // top end bracket is undefined, so we use a very large number
        if (bracket.minimumIncome <= netTaxableIncome && netTaxableIncome <= maxIncome) {
            // net income is in this bracket, so we add the amount of income in the bracket to the tax liability and jump out of the loop
            const bracketIncome = (netTaxableIncome - bracket.minimumIncome);
            taxLiability += bracketIncome * bracket.rate;
            // no need to keep going to the next bracket, we've found the correct bracket
            break;
        } else if (maxIncome <= netTaxableIncome) {
            // net income is greater than the maximum income for this bracket, so we add the entire bracket to the tax liability
            const bracketIncome = maxIncome - (bracket.minimumIncome ?? 0.0);
            taxLiability += bracketIncome * bracket.rate;
            // keep going to the next bracket
        } else {
            // net income is less than the minimum income for this bracket, so we don't add anything to the tax liability
            // should next get here since every bracket has a minimum income of 0.0.
            break;
        }
    }
    if (taxLiability < 0.0) {
        taxLiability = 0.0;
    }
    taxLiability = roundTo(-1.0 * taxLiability, 2);

    let personalOrDependentCredits = 0.0;
    switch (filingStatus) {
        case 'single':
            personalOrDependentCredits = roundTo(((stateTaxRules.personalCredit?.single ?? 0.0) + totalDependentsClaimed * (stateTaxRules.personalCredit?.perDependent ?? 0.0) + over65Deduction + blindDeduction), 2);
            break;
        case 'marriedFilingSeparately':
            personalOrDependentCredits = roundTo(((stateTaxRules.personalCredit?.marriedFilingSeparately ?? 0.0) + totalDependentsClaimed * (stateTaxRules.personalCredit?.perDependent ?? 0.0) + over65Deduction + blindDeduction), 2);
            break;
        case 'headOfHousehold':
            personalOrDependentCredits = roundTo(((stateTaxRules.personalCredit?.headOfHousehold ?? 0.0) + totalDependentsClaimed * (stateTaxRules.personalCredit?.perDependent ?? 0.0) + over65Deduction + blindDeduction), 2);
            break;
        default:
            // Default to 'single' if status is invalid (validation should catch this)
            personalOrDependentCredits = roundTo(((stateTaxRules.personalCredit?.single ?? 0.0) + totalDependentsClaimed * (stateTaxRules.personalCredit?.perDependent ?? 0.0) + over65Deduction + blindDeduction), 2);
            break;
    }

    // Get school district surtax rate
    // First try to use the surtax from the party's tax info (primary source)
    const surtaxRate = party?.tax?.schoolDistrict?.surtax ?? getSchoolDistrictSurtaxRate(party?.tax?.schoolDistrict?.id ?? "");

    // Default to 0 if surtax rate is still not available
    const finalSurtaxRate = surtaxRate ?? 0.0;
    const schoolSurtax = roundTo(-1.0 * ((Math.abs(taxLiability) - Math.abs(personalOrDependentCredits)) * finalSurtaxRate), 2);

    const finalStateTaxLiability = roundTo(Math.min(-1.0 * (Math.abs(taxLiability) - Math.abs(personalOrDependentCredits) + Math.abs(schoolSurtax)), 0.0), 2);


    return {
        grossTaxableIncome: grossIncome,
        lessHalfFicaTax: halfFicaTax,
        lessFederalTaxLiability: 0.0, // in 2025, this is always $0.00
        lessStandardDeduction: 0.0, // in 2025, this is always $0.00
        lessFederalQBIDeduction: federalQBIDeduction,
        netTaxableIncome: netTaxableIncome,
        taxLiability: taxLiability,
        lessPersonalOrDependentCredits: personalOrDependentCredits,
        schoolDistrictSurtaxRate: finalSurtaxRate,
        plusSchoolDistrictSurtax: schoolSurtax,
        finalTaxLiability: finalStateTaxLiability,
        debug: {
            wages: wages,
            selfEmployment: selfEmployment,
            otherIncomes: otherIncomes,
            pensionDeductions: pensionDeductions,
            spousalSupportReceivedYearly: spousalSupportReceivedYearly,
            spousalSupportPaidYearly: spousalSupportPaidYearly,
            halfFicaTax: halfFicaTax,
        }
    };
}

function calcSocialSecurityPensionDeduction(caseData: CaseData, oddIfAlternating: boolean, party: PartyModel | null): SocialSecurityPensionDeductionModel {

    const yearInfo = getYearInfo(caseData.children.taxSchedule ?? '', oddIfAlternating, party);
    const filing = yearInfo?.status ?? 'single';

    const wages = (party?.income?.primary?.yearlyWages ?? 0.0);
    const selfEmployment = (party?.income?.primary?.yearlySelfEmployment ?? 0.0);
    const otherIncomes = sumBy<AlternativeIncomeModel>(party?.income?.others ?? [], x => x?.isFica === true ? (x?.amount ?? 0.0) : 0.0);
    const pensionDeductionsNoWages = (sumBy<PensionContributionModel>(party?.pensions ?? [], x => x?.isIncludedInGrossWages === false && x?.isFicaApplicable === true ? (x?.computedAmount ?? (x?.income ?? 0.0) * (x?.rate ?? 0.0)) : 0.0));
    const pensionDeductionsWithWages = (sumBy<PensionContributionModel>(party?.pensions ?? [], x => x?.isIncludedInGrossWages === true && x?.isFicaApplicable === false ? (x?.computedAmount ?? (x?.income ?? 0.0) * (x?.rate ?? 0.0)) : 0.0));

    const applicablePensionRates = (party?.pensions ?? []).filter(x => x?.rate !== null && x?.rate !== undefined);
    const avgPensionRate = avgBy<PensionContributionModel>(applicablePensionRates, x => x?.rate ?? 0.0);

    const childCareReimbursements = 0.0;

    const ficaWages = roundTo(wages + otherIncomes + pensionDeductionsNoWages + childCareReimbursements - Math.abs(pensionDeductionsWithWages), 2);

    const seAdjusted = roundTo(selfEmployment * 0.9235, 2);
    const ssWageThreshold = taxRules.federal.fica.socialSecurityThreshold;

    const ssWages = roundTo(0.062 * Math.min(ficaWages, ssWageThreshold), 2);
    const remainingBase = ficaWages >= ssWageThreshold ? 0.0 : roundTo(ssWageThreshold - ficaWages, 2);

    const ssMinusSe = 0.124 * Math.min(seAdjusted, remainingBase);
    const ssTotal = ssWages + ssMinusSe;

    const medWages = 0.0145 * ficaWages;
    const medSe = 0.029 * seAdjusted;

    const medThreshold = filing === 'single' ? 200_000
        : filing === 'marriedFilingSeparately' ? 125_000
            : filing === 'headOfHousehold' ? 200_000
                : 250_000; // married_filing_jointly or unknown, neither of which should ever happen!

    const addlMedWages = roundTo(0.009 * Math.max(0.0, ficaWages - medThreshold), 2);

    const medThresholdRemaining = roundTo(Math.max(0.0, medThreshold - ficaWages), 2);

    const addlMedSe = roundTo(0.009 * Math.max(0.0, seAdjusted - medThresholdRemaining), 2);


    const annualEarnedIncome = roundTo(ficaWages + selfEmployment, 2);
    const applicableRate = roundTo(avgPensionRate * 100.0, 2);
    const applicableRateText = ((selfEmployment ?? 0.0) === 0.0) ? '7.65%'
        : ((ficaWages ?? 0.0) === 0.0) ? '15.3%'
            : '7.65% / 15.3%';


    const annualSocialSecurityAndMedicareTaxLiability = roundTo(-1.0 * (ssTotal + medWages + medSe + addlMedWages + addlMedSe), 2);


    const rv: SocialSecurityPensionDeductionModel = {
        annualEarnedIncome: annualEarnedIncome,
        applicableRate: applicableRate,
        applicableRateText: applicableRateText,
        annualSocialSecurityAndMedicareTaxLiability: annualSocialSecurityAndMedicareTaxLiability,
        debug: {
            ficaWages: ficaWages,
            selfEmployment: selfEmployment,
            seAdjusted: seAdjusted,
            ssWageThreshold: ssWageThreshold,
            ssWages: ssWages,
            remainingBase: remainingBase,
            ssMinusSe: ssMinusSe,
            ssTotal: ssTotal,
            medWages: medWages,
            medSe: medSe,
            medThreshold: medThreshold,
            addlMedWages: addlMedWages,
            medThresholdRemaining: medThresholdRemaining,
            addlMedSe: addlMedSe
        }
    };

    return rv;

}

function calcOtherDeductions(caseData: CaseData, oddIfAlternating: boolean, party: PartyModel | null, sourcesAndAmounts: SourcesAndAmountsModel, federalTaxDeductions: FederalIncomeModel, stateTaxDeductions: StateIncomeModel, taxRules: TaxRules): OtherDeductionsModel {

    const federalTaxRules = taxRules.federal;
    const stateTaxRules = taxRules.states[caseData.basic.stateCode ?? 'IA'];

    const yearInfo = getYearInfo(caseData.children.taxSchedule ?? '', oddIfAlternating, party);
    const filingStatus = yearInfo?.status ?? 'single'; // Default to 'single' if missing (validation should catch this)


    const licenseFees = roundTo(-1.0 * (party?.deductions?.licenseFees ?? 0.0), 2);
    const unionDues = roundTo(-1.0 * (party?.deductions?.unionDues ?? 0.0), 2);
    const actualMedicalSupportPaidPerCourtOrder = roundTo(-1.0 * (party?.deductions?.priorMedicalSupportOrder ?? 0.0), 2);
    const actualPriorObligationChildSupportPaidPerCourtOrder = roundTo(-1.0 * (party?.deductions?.priorChildSupportOrder ?? 0.0), 2);

    // find the qadd level for the number of additional dependents (if not found, assume chlidren is over maximum (typically 5 or more))
    const qaddLevel = stateTaxRules.qadd.find(x => x?.count === (yearInfo?.additional ?? 0)) ?? stateTaxRules.qadd[stateTaxRules.qadd.length - 1];
    const qaddDeductions = roundTo(-1.0 * Math.min((sourcesAndAmounts.total * qaddLevel.rate), qaddLevel.minimum), 2);

    const childrenInChildCare = party?.deductions?.childrenInChildCareCount ?? 0;

    // lookup up the child care limit based on the number of children in child care
    const childCareLimit = childrenInChildCare > 1 ? federalTaxRules.dependentCare.limitTwoOrMoreDependents : childrenInChildCare > 0 ? federalTaxRules.dependentCare.limitOneDependent : 0.0;

    const childCareExpensesForPresentAction = roundTo(-1.0 * (party?.deductions?.yearlyChildCareExpenses ?? 0.0), 2);
    const childCareReimbursements = roundTo(-1.0 * (party?.deductions?.childCareReimbursements ?? 0.0), 2);

    // eligible expenses is the minimum of the child care expenses for present action and the child care limit, minus the child care reimbursements
    const eligibleExpenses = roundTo(-1.0 * Math.min(childCareExpensesForPresentAction, childCareLimit) - Math.abs(childCareReimbursements), 2);


    // calculate the effective income for the federal tax credits
    const federalAgi = sourcesAndAmounts.total - Math.abs(federalTaxDeductions.lessHalfFicaTax);
    const effectiveIncome = roundTo(federalAgi - 15_000, 2);
    const adjustedPct = Math.floor(effectiveIncome / 2_000.0) * 0.01;
    const federalPct = Math.max(0.20, 0.35 - Math.abs(adjustedPct));
    const federalTaxCredits = roundTo(Math.abs(eligibleExpenses * federalPct), 2);

    const partyIsCustodial = isCustodialParent(caseData, party);

    const stateIncomeForLookup = roundTo(Math.abs(stateTaxDeductions.netTaxableIncome) + Math.abs(stateTaxDeductions.lessStandardDeduction), 2);
    const lookupRate = stateDependentCareLevels.find(x => x.minimumIncome <= stateIncomeForLookup && x.maximumIncome >= stateIncomeForLookup)?.percentage ?? 0.0;

    const stateTaxDependentCareCredits = roundTo(-1.0 * (Math.abs(federalTaxCredits) * lookupRate), 2);
    const stateTaxRefund = 0.0; // in 2025 and later, this is always $0.00

    // finally, use all that info to calculate the actual child care expenses
    const actualChildCareExpenses = roundTo(-1.0 * Math.max(0.0, Math.abs(childCareExpensesForPresentAction) - Math.abs(federalTaxCredits) - Math.abs(stateTaxDependentCareCredits) - Math.abs(stateTaxRefund) - Math.abs(childCareReimbursements)), 2);


    const rv: OtherDeductionsModel = {
        mandatoryOccupationalLicenseFees: licenseFees,
        unionDues: unionDues,
        actualMedicalSupportPaidPerCourtOrder: actualMedicalSupportPaidPerCourtOrder,
        actualPriorObligationChildSupportPaidPerCourtOrder: actualPriorObligationChildSupportPaidPerCourtOrder,
        deductionsForQadd: qaddDeductions,
        childCareExpensesForPresentAction: partyIsCustodial ? childCareExpensesForPresentAction : 0.0,
        lessFederalTaxCredits: partyIsCustodial ? federalTaxCredits : 0.0,
        lessStateTaxCredits: partyIsCustodial ? stateTaxDependentCareCredits : 0.0,
        lessStateTaxRefund: partyIsCustodial ? stateTaxRefund : 0.0,
        lessThirdPartyReimbursements: partyIsCustodial ? childCareReimbursements : 0.0,
        actualChildCareExpenses: partyIsCustodial ? actualChildCareExpenses : 0.0,
        debug: {
            partyIsCustodial: partyIsCustodial,
            qaddLevel: qaddLevel,
            childrenInChildCare: childrenInChildCare,
            childCareLimit: childCareLimit,
            eligibleExpenses: eligibleExpenses,
            federalPct: federalPct,
            federalAdjustedGrossIncome: federalAgi,
            effectiveIncome: effectiveIncome,
            adjustedPct: adjustedPct,
            stateIncomeForDependentCareLookup: stateIncomeForLookup,
            stateDependentCareRate: lookupRate,
            stateTaxDependentCareCredits: stateTaxDependentCareCredits,

            calcChildCareExpensesForPresentAction: childCareExpensesForPresentAction,
            calcLessFederalTaxCredits: federalTaxCredits,
            calcLessStateTaxCredits: stateTaxDependentCareCredits,
            calcLessStateTaxRefund: stateTaxRefund,
            calcLessThirdPartyReimbursements: childCareReimbursements,
            calcActualChildCareExpenses: actualChildCareExpenses,
        }
    }

    return rv;
}

function needToCalcMedical(caseData: CaseData): Boolean {

    // if shared care, NO cash medical calc
    // if either party has a special exemption, NO medical calc.
    // if either party has Title XIX insurance, YES medical calc.
    // if either party has Private Pay insurance, NO medical calc.
    // if either party has No Cost Insurance, NO medical calc.
    // if either party has HAWK-I insurance, YES medical calc.
    // if either party has No Insurance, YES medical calc.
    // otherwise assume YES medical calc.

    if (caseData.children.custodyArrangement === 'shared') {
        return false;
    }

    const partyAMedical = caseData.parties?.partyA?.medical;
    const partyBMedical = caseData.parties?.partyB?.medical;

    if (partyAMedical?.isSpecialExemptionApplicable === true || partyBMedical?.isSpecialExemptionApplicable === true) {
        return false;
    }


    const partyAInsuranceType = partyAMedical?.coverageTypeId ?? '';
    const partyBInsuranceType = caseData.parties?.partyB?.medical?.coverageTypeId ?? '';

    if (partyAInsuranceType === 'titleXIX' || partyBInsuranceType === 'titleXIX') {
        //     Title XIX on either party trumps everything, DOES trigger cash medical.
        return true;
    } else if (partyAInsuranceType === 'privatePay' || partyBInsuranceType === 'privatePay') {
        //     Private Pay on either party trumps everything else except other private pay. Does NOT trigger cash medical.
        return false;
    } else if (partyAInsuranceType === 'noCost' || partyBInsuranceType === 'noCost') {
        //     No Cost insurance, does NOT trigger cash medical.
        return false;
    } else if (partyAInsuranceType === 'hawkI' || partyBInsuranceType === 'hawkI') {
        //     Hawk-I insurance, DOES trigger cash medical. (Equals MIN(cash medical, Hawk-I))
        return true;
    } else if (partyAInsuranceType === 'none' || partyBInsuranceType === 'none') {
        //     No insurance, DOES trigger cash medical.
        return true;
    } else if (partyAInsuranceType === '' && partyBInsuranceType === '') {
        //     If neither are specified, DOES trigger cash medical.
        return true;
    }
    return false;
}



function calcTotalsSection(caseData: CaseData, party: PartyModel | null, netMonthlyIncomeSection: NetMonthlyIncomeModel, sourcesAndAmounts: SourcesAndAmountsModel, federalTaxDeductions: FederalIncomeModel, stateTaxDeductions: StateIncomeModel, socialSecurityPensionDeductions: SocialSecurityPensionDeductionModel, otherDeductionsSection: OtherDeductionsModel): TotalsModel {

    /*
        Value	                Source Location	
        Gross Annual Income	    Section A - TOTAL
        Final Federal Tax	    Section B	
        Final State Tax	        Section C	
        FICA Taxes	            Section D	
        License Fees	        Section E.1	
        Union Dues	            Section E.2	
        Prior Medical	        Section E.3	
        Prior Child Support	    Section E.4	
        QADD Deduction	        Section E.5	
        Refundable Credit	    Not shown	
        Actual Child Care	    Section E.6	
        Reimbursements	        Section E.6	

    */
    const grossAnnualIncome = sourcesAndAmounts.total;
    const federalTaxLiability = federalTaxDeductions.finalTaxLiability;
    const stateTaxLiability = stateTaxDeductions.finalTaxLiability;
    const ficaTaxLiability = socialSecurityPensionDeductions.annualSocialSecurityAndMedicareTaxLiability;
    const licenseFees = otherDeductionsSection.mandatoryOccupationalLicenseFees;
    const unionDues = otherDeductionsSection.unionDues;
    const priorMedicalSupport = otherDeductionsSection.actualMedicalSupportPaidPerCourtOrder;
    const priorChildSupport = otherDeductionsSection.actualPriorObligationChildSupportPaidPerCourtOrder;
    const qaddDeduction = otherDeductionsSection.deductionsForQadd;
    const refundableCredit = 0.0; // in 2025, this is always $0.00
    const actualChildCare = otherDeductionsSection.actualChildCareExpenses;
    //const reimbursements = otherDeductionsSection.lessThirdPartyReimbursements;

    const totalDeductions = roundTo(licenseFees + unionDues + priorMedicalSupport + priorChildSupport + qaddDeduction + actualChildCare, 2);

    // calculate the preliminary net annual incomeconst maxRefundableAmount
    const maxRefundableAmount = roundTo(Math.abs(federalTaxDeductions.childTaxCreditRefundable ?? 0.0), 2);
    const earnedIncomeLimit = roundTo(Math.abs(federalTaxDeductions.debug.earnedIncomeLimit ?? 0.0), 2);
    const finalRefundable = roundTo(Math.min(Math.abs(maxRefundableAmount), Math.abs(earnedIncomeLimit)), 2);


    const preliminaryNetAnnualIncome = roundTo(grossAnnualIncome + federalTaxLiability + stateTaxLiability + ficaTaxLiability + totalDeductions, 2);

    const preliminaryAverageNetMonthlyIncome = roundTo(preliminaryNetAnnualIncome / 12.0, 2);



    // debugLog("calcMedical, monthlyCashmedicalSupportOrdered, insuranceType", calcMedical, monthlyCashMedicalSupportOrdered, party?.medical?.coverageTypeId );

    const rv: TotalsModel = {
        preliminaryNetAnnualIncome: preliminaryNetAnnualIncome,
        preliminaryAverageNetMonthlyIncome: preliminaryAverageNetMonthlyIncome,
        monthlyCashMedicalSupportOrdered: -999999.99, // to be set later
        adjustedNetMonthlyIncome: -999999.99, // to be set later
        debug: {
            grossAnnualIncome: grossAnnualIncome,
            federalTaxLiability: federalTaxLiability,
            stateTaxLiability: stateTaxLiability,
            ficaTaxLiability: ficaTaxLiability,
            maxRefundableAmount: maxRefundableAmount,
            earnedIncomeLimit: earnedIncomeLimit,
            finalRefundable: finalRefundable,
            licenseFees: licenseFees,
            unionDues: unionDues,
            priorMedicalSupport: priorMedicalSupport,
            priorChildSupport: priorChildSupport,
            qaddDeduction: qaddDeduction,
            refundableCredit: refundableCredit,
            actualChildCare: actualChildCare,
            totalDeductions: totalDeductions
        }
    };

    debugLog("final totalsSection value", rv);

    return rv;
}


export function calcWorksheet1Party(caseData: CaseData, oddIfAlternating: boolean, party: PartyModel | null, taxRules: TaxRules): Worksheet1PartyModel {

    const netMonthlyIncome = calcNetMonthlyIncome(caseData, oddIfAlternating, party);
    const sourcesAndAmounts = calcSourcesAndAmounts(caseData, party, netMonthlyIncome);
    const federalTaxDeductions = calcFederalTaxDeduction(caseData, oddIfAlternating, party, sourcesAndAmounts, taxRules);
    const stateTaxDeductions = calcStateTaxDeduction(caseData, oddIfAlternating, party, federalTaxDeductions, taxRules);
    const socialSecurityPensionDeductions = calcSocialSecurityPensionDeduction(caseData, oddIfAlternating, party);
    const otherDeductionsSection = calcOtherDeductions(caseData, oddIfAlternating, party, sourcesAndAmounts, federalTaxDeductions, stateTaxDeductions, taxRules);
    const totalsSection = calcTotalsSection(caseData, party, netMonthlyIncome, sourcesAndAmounts, federalTaxDeductions, stateTaxDeductions, socialSecurityPensionDeductions, otherDeductionsSection);

    const rv: Worksheet1PartyModel = {
        netMonthlyIncomeSection: netMonthlyIncome,
        sourcesAndAmountsSection: sourcesAndAmounts,
        federalTaxDeductionSection: federalTaxDeductions,
        stateTaxDeductionSection: stateTaxDeductions,
        socialSecurityPensionDeductionSection: socialSecurityPensionDeductions,
        otherDeductionsSection: otherDeductionsSection,
        totalsSection: totalsSection
    };

    return rv;

}

function calcCashMedical(caseData: CaseData, petitioner: Worksheet1PartyModel, respondent: Worksheet1PartyModel) {

    // See following diagram for details:
    // https://lucid.app/lucidchart/0a82de23-1e0f-4036-8e73-aa5760dc511d/edit?viewport_loc=-1328%2C119%2C6232%2C3026%2C0_0&invitationId=inv_404b6be3-180e-4251-acfb-81b834f1eeee

    // If exemption, no cash medical.
    // If shared, no cash medical.
    // If custodial:
    //     Title XIX on either party trumps everything, DOES trigger cash medical.
    //     Private Pay on either party trumps everything else except other private pay. Does NOT trigger cash medical.
    //     No Cost insurance, does NOT trigger cash medical.
    //     Hawk-I insurance, DOES trigger cash medical. (Equals MIN(cash medical, Hawk-I))
    //     No insurance, DOES trigger cash medical.
    //     If neither are specified, DOES trigger cash medical.

    const petitionerParty = getPetitionerParty(caseData);
    const respondentParty = getRespondentParty(caseData);

    const petitionerIsNCP = petitioner.netMonthlyIncomeSection.isNoncustodialParent;


    const custodial = petitionerIsNCP ? respondent : petitioner;
    const nonCustodial = petitionerIsNCP ? petitioner : respondent;

    const custodialParty = petitionerIsNCP ? respondentParty : petitionerParty;
    const nonCustodialParty = petitionerIsNCP ? petitionerParty : respondentParty;



    const needMedicalCalc = needToCalcMedical(caseData);

    if (!needMedicalCalc) {
        debugLog("No medical calc required");
        // nothing special to do other than set each party's monthlyCashMedicalSupportOrdered and adjustedNetMonthlyIncome properties on totalsSection object.

        petitioner.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
        petitioner.totalsSection.adjustedNetMonthlyIncome = roundTo(petitioner.totalsSection.preliminaryAverageNetMonthlyIncome, 2);

        respondent.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
        respondent.totalsSection.adjustedNetMonthlyIncome = roundTo(respondent.totalsSection.preliminaryAverageNetMonthlyIncome, 2);
    } else {
        debugLog("Medical calc IS required");
        if (custodialParty?.medical?.coverageTypeId === 'hawkI' && (custodialParty?.medical?.monthlyMedicalCost ?? 0.0) > 0.0) {
            debugLog("CP has hawk-i with cost of ", custodialParty?.medical?.monthlyMedicalCost);

            custodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            custodial.totalsSection.adjustedNetMonthlyIncome = roundTo(custodial.totalsSection.preliminaryAverageNetMonthlyIncome, 2);

            const medicalSupportLevel = getCashMedicalSupportLevel(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome);
            if (medicalSupportLevel?.isCashMedicalSupport === true) {
                const temp = roundTo((medicalSupportLevel.rates[(caseData.children.count ?? 0) - 1] * nonCustodial.sourcesAndAmountsSection.total) / 12.0, 2);
                nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered = roundTo(Math.min(temp, custodialParty?.medical?.monthlyMedicalCost ?? 0.0))
                //;
            } else {
                nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            }
            nonCustodial.totalsSection.adjustedNetMonthlyIncome = roundTo(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome - Math.abs(nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered ?? 0.0))

        } else if (custodialParty?.medical?.coverageTypeId === 'titleXIX' || nonCustodialParty?.medical?.coverageTypeId === 'titleXIX') {
            debugLog("CP OR NCP has titleXIX");

            custodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            custodial.totalsSection.adjustedNetMonthlyIncome = roundTo(custodial.totalsSection.preliminaryAverageNetMonthlyIncome, 2); ///!!!!!!

            const medicalSupportLevel = getCashMedicalSupportLevel(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome);
            if (medicalSupportLevel?.isCashMedicalSupport === true) {
                nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered = roundTo((medicalSupportLevel.rates[(caseData.children.count ?? 0) - 1] * nonCustodial.sourcesAndAmountsSection.total) / 12.0, 2);
                //;
            } else {
                nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            }
            nonCustodial.totalsSection.adjustedNetMonthlyIncome = roundTo(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome - Math.abs(nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered ?? 0.0))

        } else if (custodialParty?.medical?.coverageTypeId === 'none' || nonCustodialParty?.medical?.coverageTypeId === 'none' as MedicalCoverageType) {
            debugLog("CP OR NCP has none");

            custodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            custodial.totalsSection.adjustedNetMonthlyIncome = roundTo(custodial.totalsSection.preliminaryAverageNetMonthlyIncome, 2);

            const medicalSupportLevel = getCashMedicalSupportLevel(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome);
            if (medicalSupportLevel?.isCashMedicalSupport === true) {
                nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered = roundTo((medicalSupportLevel.rates[(caseData.children.count ?? 0) - 1] * nonCustodial.sourcesAndAmountsSection.total) / 12.0, 2);
                //;
            } else {
                nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            }
            nonCustodial.totalsSection.adjustedNetMonthlyIncome = roundTo(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome - Math.abs(nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered ?? 0.0))

        } else {
            // no cash medical 
            petitioner.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            petitioner.totalsSection.adjustedNetMonthlyIncome = roundTo(petitioner.totalsSection.preliminaryAverageNetMonthlyIncome, 2);

            respondent.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
            respondent.totalsSection.adjustedNetMonthlyIncome = roundTo(respondent.totalsSection.preliminaryAverageNetMonthlyIncome, 2);
        }

        // } else if (nonCustodialParty?.medical?.coverageTypeId === 'hawk_i' && (nonCustodialParty?.medical?.monthlyMedicalCost ?? 0.0) > 0.0) {
        //     debugLog("NCP has hawk-i with cost of ", nonCustodialParty?.medical?.monthlyMedicalCost);

        //     nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
        //     nonCustodial.totalsSection.adjustedNetMonthlyIncome = roundTo(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome, 2);

        //     const medicalSupportLevel = getCashMedicalSupportLevel(nonCustodial.totalsSection.preliminaryAverageNetMonthlyIncome);
        //     if (medicalSupportLevel?.isCashMedicalSupport === true) {
        //         custodial.totalsSection.monthlyCashMedicalSupportOrdered = roundTo((medicalSupportLevel.rates[(caseData.children.count ?? 0) - 1] * nonCustodial.sourcesAndAmountsSection.total) / 12.0, 2);
        //     } else {
        //         custodial.totalsSection.monthlyCashMedicalSupportOrdered = 0.0;
        //     }
        //     custodial.totalsSection.monthlyCashMedicalSupportOrdered = roundTo(Math.min(nonCustodial.totalsSection.monthlyCashMedicalSupportOrdered, custodialParty?.medical?.monthlyMedicalCost ?? 0.0));

        //     custodial.totalsSection.adjustedNetMonthlyIncome = roundTo(custodial.totalsSection.preliminaryAverageNetMonthlyIncome - Math.abs(custodial.totalsSection.monthlyCashMedicalSupportOrdered ?? 0.0))


    }

}

export function calcWorksheet1Section3(caseData: CaseData, petitioner: Worksheet1PartyModel, respondent: Worksheet1PartyModel): Worksheet1Section3Model {


    // first off: calculate cash medical. This is fairly involved and does
    // not use any values from the child support calculation results below.
    calcCashMedical(caseData, petitioner, respondent);


    const petitionerParty = getPetitionerParty(caseData);
    const respondentParty = getRespondentParty(caseData);

    const petitionerIsNCP = petitioner.netMonthlyIncomeSection.isNoncustodialParent;
    const respondentIsNCP = respondent.netMonthlyIncomeSection.isNoncustodialParent;



    const petitionerCustodialText = petitioner.netMonthlyIncomeSection.isCustodialParent ? 'CP'
        : petitionerIsNCP ? 'NCP'
            : petitioner.netMonthlyIncomeSection.isShared ? 'Shared'
                : petitioner.netMonthlyIncomeSection.isSplit ? 'Split'
                    : 'Unknown';

    const petitionerLineA = roundTo(petitioner.totalsSection.adjustedNetMonthlyIncome, 2);
    const respondentLineA = roundTo(respondent.totalsSection.adjustedNetMonthlyIncome, 2);
    const combinedLineA = roundTo(petitionerLineA + respondentLineA, 2);

    const petitionerLineB = combinedLineA !== 0 ? (petitionerLineA / combinedLineA) * 100.0 : 0;


    const respondentCustodialText = respondent.netMonthlyIncomeSection.isCustodialParent ? 'CP'
        : respondentIsNCP ? 'NCP'
            : respondent.netMonthlyIncomeSection.isShared ? 'Shared'
                : respondent.netMonthlyIncomeSection.isSplit ? 'Split'
                    : 'Unknown';

    const respondentLineB = combinedLineA !== 0 ? (respondentLineA / combinedLineA) * 100.0 : 0;

    const combinedLineB = roundTo((petitionerLineB + respondentLineB), 2);

    const combinedLineC = caseData.children.count ?? 0;

    const levelOffset = Math.min(caseData.children.count ?? 0, 5) - 1;

    const petitionerChildSupportLevel = getChildSupportLevel(roundTo(petitionerLineA ?? 0.0, 2));
    const petitionerIsLowIncome = petitionerChildSupportLevel.lowIncomes[levelOffset] === true;
    const petitionerLineD = petitionerIsLowIncome ? petitionerChildSupportLevel.amounts[levelOffset] : 0.0;
    const petitionerIncomeArea = petitionerChildSupportLevel.incomeArea;

    const respondentChildSupportLevel = getChildSupportLevel(roundTo(respondentLineA ?? 0.0, 2));
    const respondentIsLowIncome = respondentChildSupportLevel.lowIncomes[levelOffset] === true;
    const respondentLineD = respondentIsLowIncome ? respondentChildSupportLevel.amounts[levelOffset] : 0.0;
    const respondentIncomeArea = respondentChildSupportLevel.incomeArea;


    const childSupportLevel = getChildSupportLevel(roundTo(combinedLineA ?? 0.0, 2));

    const guidelineAmountIndex = Math.max(0, Math.min(combinedLineC, 5) - 1);
    const combinedLineE = childSupportLevel.amounts[guidelineAmountIndex];


    // line D always contains the lookup value.
    // line F always contains the calculated value.
    // line G uses the income area to determine which value to use or the minimum of the two.
    // if income area is 'a' then line G is line D.
    // if income area is 'b' and the parent is low income, then line G is MIN(line D, line F).
    // if income area is 'b' and the parent is NOT low income, then line G is line F.
    // if income area is 'c' then line G is line F.
    const petitionerLineF = roundTo((combinedLineE * petitionerLineB) / 100.0, 2);
    const respondentLineF = roundTo((combinedLineE * respondentLineB) / 100.0, 2);

    //     let petitionerLineF = 0.0;
    // let respondentLineF = 0.0;
    // if (childSupportLevel.lowIncomes[combinedLineC - 1]) {
    //     // low income flag is set. straight lookup, no calc.
    //     petitionerLineF = roundTo(childSupportLevel.amounts[combinedLineC - 1], 2);
    //     respondentLineF = roundTo(childSupportLevel.amounts[combinedLineC - 1], 2);
    // } else {
    // }

    let petitionerLineG = 0.0;
    let respondentLineG = 0.0;

    if (petitionerIsNCP) {
        // peitioner is the noncustodial parent.
        switch (petitionerIncomeArea) {
            case 'a':
                petitionerLineG = petitionerLineD;
                break;
            case 'b':
                if (petitionerIsLowIncome) {
                    petitionerLineG = roundTo(Math.min(petitionerLineD, petitionerLineF), 2);
                } else {
                    petitionerLineG = petitionerLineF;
                }
                break;
            case 'c':
                petitionerLineG = petitionerLineF;
                break;
            default:
                petitionerLineG = -999999.99;
                break;
        }
    }


    if (respondentIsNCP) {
        // peitioner is the noncustodial parent.
        switch (respondentIncomeArea) {
            case 'a':
                respondentLineG = respondentLineD;
                break;
            case 'b':
                if (respondentIsLowIncome) {
                    respondentLineG = roundTo(Math.min(respondentLineD, respondentLineF), 2);
                } else {
                    respondentLineG = respondentLineF;
                }
                break;
            case 'c':
                respondentLineG = respondentLineF;
                break;
            default:
                respondentLineG = -999999.99;
                break;
        }
    }

    const petitionerMedical = getPetitionerParty(caseData)?.medical;
    const respondentMedical = getRespondentParty(caseData)?.medical;


    const petitionerLineH = (petitionerParty?.medical?.coverageTypeId === 'privatePay' || petitionerParty?.medical?.coverageTypeId === 'hawkI') ? (petitionerMedical?.monthlyMedicalCost ?? 0.0) : 0.0;
    const respondentLineH = (respondentParty?.medical?.coverageTypeId === 'privatePay' || respondentParty?.medical?.coverageTypeId === 'hawkI') ? (respondentMedical?.monthlyMedicalCost ?? 0.0) : 0.0;


    let petitionerLineI = 0.0;
    let petitionerLineIText = '';
    let respondentLineI = 0.0;
    let respondentLineIText = '';

    const healthInsuranceDifference = roundTo(Math.abs(petitionerLineH - respondentLineH), 2);

    if (petitionerIsNCP) {
        // peitioner is the noncustodial parent.
        if (petitionerLineH > respondentLineH) {
            // petitioner is NCP and paying more than respondent. they get a deduction.
            petitionerLineI = roundTo(-1.0 * healthInsuranceDifference * respondentLineB / 100.0, 2);
            petitionerLineIText = petitionerMedical?.coverageTypeId ?? '';
        } else {
            // petitioner is NCP and paying less than the respondent. they pay their percentage of the difference.
            petitionerLineI = roundTo(healthInsuranceDifference * petitionerLineB / 100.0, 2);
        }
    }

    if (respondentIsNCP) {
        // respondent is the noncustodial parent.
        if (respondentLineH > petitionerLineH) {
            // respondent is NCP and paying more than petitioner. they get a deduction.
            respondentLineI = roundTo(-1.0 * healthInsuranceDifference * petitionerLineB / 100.0, 2);
            respondentLineIText = respondentMedical?.coverageTypeId ?? '';
        } else {
            // respondent is NCP and paying less than the petitioner. they pay their percentage of the difference.
            respondentLineI = roundTo(healthInsuranceDifference * respondentLineB / 100.0, 2);
        }
    }




    let petitionerLineJ1 = roundTo(petitionerLineG + petitionerLineI, 2);
    // if (petitionerIsNCP) {
    //     if (petitionerIncomeArea === 'a') {
    //         petitionerLineJ1 = roundTo(petitionerLineD + petitionerLineI, 2);
    //     } else if (petitionerIncomeArea === 'b') {
    //         petitionerLineJ1 = roundTo(Math.min(petitionerLineD + petitionerLineI, petitionerLineJ1), 2);
    //     }
    // }

    let respondentLineJ1 = roundTo(respondentLineG + respondentLineI, 2);
    // if (respondentIsNCP) {
    //     if (respondentIncomeArea === 'a') {
    //         respondentLineJ1 = roundTo(respondentLineD + respondentLineI, 2);
    //     } else if (respondentIncomeArea === 'b') {
    //         respondentLineJ1 = roundTo(Math.min(respondentLineD + respondentLineI, respondentLineJ1), 2);
    //     }
    // }

    const petitionerLineJ2 = (respondentParty?.deductions?.isRequestingVariance ?? false) ? respondentParty?.deductions?.variancePayment ?? 0.0 : 0.0;
    const petitionerLineJ3 = petitionerLineJ1 + petitionerLineJ2;

    const respondentLineJ2 = (petitionerParty?.deductions?.isRequestingVariance ?? false) ? petitionerParty?.deductions?.variancePayment ?? 0.0 : 0.0;
    const respondentLineJ3 = respondentLineJ1 + respondentLineJ2;

    const petitionerLineJ4 = petitioner.totalsSection.monthlyCashMedicalSupportOrdered;
    const respondentLineJ4 = respondent.totalsSection.monthlyCashMedicalSupportOrdered;




    let temporaryChildSupportAmount = petitionerIsNCP ? petitionerLineJ3 : respondentLineJ3;


    let petitionerLineK = 0.0;
    let petitionerLineL = '';
    let petitionerLineM = 0.0;
    let petitionerLineN = 0.0;
    let petitionerLineO = 0.0;


    let respondentLineK = 0.0;
    let respondentLineL = '';
    let respondentLineM = 0.0;
    let respondentLineN = 0.0;
    let respondentLineO = 0.0;

    const overnightsPerYear = caseData.children.overnightsPerYear ?? '';

    if (caseData.children.custodyArrangement === 'partyA' || caseData.children.custodyArrangement === 'partyB') {
        if (overnightsPerYear === '128To147' || overnightsPerYear === '148To166' || overnightsPerYear === '167OrMore') {
            // this is extraOrdinary visitation credit.
            if (petitionerIsNCP) {
                // petitioner is the noncustodial parent.
                petitionerLineK = petitionerLineG;
                petitionerLineL = '0 - 127';

                if (overnightsPerYear === '128To147') {
                    petitionerLineL = '128 - 147';
                    petitionerLineM = 15.0;
                } else if (overnightsPerYear === '148To166') {
                    petitionerLineL = '148 - 166';
                    petitionerLineM = 20.0;
                } else if (overnightsPerYear === '167OrMore') {
                    petitionerLineL = '167 or more';
                    petitionerLineM = 25.0;
                }

                petitionerLineN = roundTo(petitionerLineK * petitionerLineM / 100.0, 2);
                petitionerLineO = roundTo(petitionerLineJ3 - petitionerLineN, 2);

                temporaryChildSupportAmount = petitionerLineO;
            }
            if (respondentIsNCP) {
                // respondent is the noncustodial parent.
                respondentLineK = respondentLineG;
                respondentLineL = '0 - 127';

                if (overnightsPerYear === '128To147') {
                    respondentLineL = '128 - 147';
                    respondentLineM = 15.0;
                } else if (overnightsPerYear === '148To166') {
                    respondentLineL = '148 - 166';
                    respondentLineM = 20.0;
                } else if (overnightsPerYear === '167OrMore') {
                    respondentLineL = '167 or more';
                    respondentLineM = 25.0;
                }

                respondentLineN = roundTo(respondentLineK * respondentLineM / 100.0, 2);
                respondentLineO = roundTo(respondentLineJ3 - respondentLineN, 2);

                temporaryChildSupportAmount = respondentLineO;
            }
        }
    }



    // const childrenCount = petitioner.netMonthlyIncomeSection.children0to16 + petitioner.netMonthlyIncomeSection.children17Plus;
    // const petitionerCashMedicalSupportLevel = getCashMedicalSupportVersioned(caseData, petitionerLineA);
    // const petitionerCashMedicalSupportRate = petitionerCashMedicalSupportLevel?.rates[childrenCount - 1] ?? 0.0;
    // const petitionerCashMedicalSupportProvided = petitionerCashMedicalSupportLevel?.isCashMedicalSupport === true;


    const rv: Worksheet1Section3Model = {
        petitioner: {
            custodialText: petitionerCustodialText,
            isCustodialParent: petitioner.netMonthlyIncomeSection.isCustodialParent,
            isNoncustodialParent: petitioner.netMonthlyIncomeSection.isNoncustodialParent,
            isShared: petitioner.netMonthlyIncomeSection.isShared,
            isSplit: petitioner.netMonthlyIncomeSection.isSplit,
            isLowIncome: petitionerIsLowIncome,
            lineA: petitionerLineA,
            lineB: petitionerLineB,
            //lineC: combinedLineC,
            lineD: petitionerIsNCP ? petitionerLineD : 0.0,
            lineDText: petitionerIncomeArea,
            lineE: 0.0,
            lineF: petitionerLineF,
            lineG: petitionerLineG,
            lineH: petitionerLineH,
            lineI: petitionerLineI,
            lineIText: petitionerLineIText,
            lineJ1: petitionerLineJ1,
            lineJ2: petitionerLineJ2,
            lineJ3: petitionerLineJ3,
            lineJ4: petitionerLineJ4,
            lineK: petitionerLineK,
            lineL: petitionerLineL,
            lineM: petitionerLineM,
            lineN: petitionerLineN,
            lineO: petitionerLineO,
            finalChildSupportAmount: petitionerIsNCP ? temporaryChildSupportAmount : 0.0,
            finalCashMedicalSupportAmount: petitionerLineJ4,
            debug: {
                childSupportLevel: petitionerChildSupportLevel,
                isLowIncome: petitionerIsLowIncome,
                incomeArea: petitionerIncomeArea
            }
        },
        respondent: {
            custodialText: respondentCustodialText,
            isCustodialParent: respondent.netMonthlyIncomeSection.isCustodialParent,
            isNoncustodialParent: respondent.netMonthlyIncomeSection.isNoncustodialParent,
            isShared: respondent.netMonthlyIncomeSection.isShared,
            isSplit: respondent.netMonthlyIncomeSection.isSplit,
            isLowIncome: respondentIsLowIncome,
            lineA: respondentLineA,
            lineB: respondentLineB,
            //lineC: combinedLineC,
            lineD: respondentIsNCP ? respondentLineD : 0.0,
            lineE: 0.0,
            lineF: respondentLineF,
            lineG: respondentLineG,
            lineH: respondentLineH,
            lineI: respondentLineI,
            lineIText: respondentLineIText,
            lineJ1: respondentLineJ1,
            lineJ2: respondentLineJ2,
            lineJ3: respondentLineJ3,
            lineJ4: respondentLineJ4,
            lineK: respondentLineK,
            lineL: respondentLineL,
            lineM: respondentLineM,
            lineN: respondentLineN,
            lineO: respondentLineO,
            finalChildSupportAmount: respondentIsNCP ? temporaryChildSupportAmount : 0.0,
            finalCashMedicalSupportAmount: respondentLineJ4,
            debug: {
                childSupportLevel: respondentChildSupportLevel,
                isLowIncome: respondentIsLowIncome,
                incomeArea: respondentIncomeArea
            }

        },
        combined: {
            lineA: combinedLineA,
            lineB: combinedLineB,
            lineC: combinedLineC,
            lineD: 0.0,
            lineE: combinedLineE,
        }
    };

    return rv;
}

export function calcWorksheet1Section4(caseData: CaseData, petitioner: Worksheet1PartyModel, respondent: Worksheet1PartyModel): Worksheet1Section4Model {

    // BEGIN CHUNK THAT IS THE SAME AS SECTION 3.

    const petitionerParty = getPetitionerParty(caseData);
    const respondentParty = getRespondentParty(caseData);

    const petitionerLineA = roundTo(petitioner.totalsSection.adjustedNetMonthlyIncome, 2);
    const respondentLineA = roundTo(respondent.totalsSection.adjustedNetMonthlyIncome, 2);

    const combinedLineA = roundTo(petitionerLineA + respondentLineA, 2);

    const petitionerLineB = combinedLineA !== 0 ? (petitionerLineA / combinedLineA) * 100.0 : 0;


    const respondentLineB = combinedLineA !== 0 ? (respondentLineA / combinedLineA) * 100.0 : 0;

    const combinedLineB = roundTo((petitionerLineB + respondentLineB), 2);

    const combinedLineC = caseData.children.count ?? 0;

    const childSupportLevel = getChildSupportLevel(roundTo(combinedLineA ?? 0.0, 2));

    const guidelineAmountIndex = Math.max(0, Math.min(combinedLineC, 5) - 1);
    const combinedLineD = childSupportLevel.amounts[guidelineAmountIndex];

    const petitionerLineE = roundTo((combinedLineD * petitionerLineB) / 100.0, 2);
    const respondentLineE = roundTo((combinedLineD * respondentLineB) / 100.0, 2);

    const petitionerLineF = roundTo(petitionerLineE * 1.5, 2);
    const respondentLineF = roundTo(respondentLineE * 1.5, 2);

    const petitionerLineG = roundTo(petitionerLineF / 2.0, 2);
    const respondentLineG = roundTo(respondentLineF / 2.0, 2);

    const petitionerMedical = getPetitionerParty(caseData)?.medical;
    const respondentMedical = getRespondentParty(caseData)?.medical;

    // only show monthly medical cost for the party if the coverage type is privatePay or hawkI.
    var petitionerLineH = petitionerParty?.medical?.coverageTypeId === 'privatePay' || petitionerParty?.medical?.coverageTypeId === 'hawkI' ? petitionerMedical?.monthlyMedicalCost ?? 0.0 : 0.0;
    var respondentLineH = respondentParty?.medical?.coverageTypeId === 'privatePay' || respondentParty?.medical?.coverageTypeId === 'hawkI' ? respondentMedical?.monthlyMedicalCost ?? 0.0 : 0.0;

    var petitionerLineHText = convertMedicalCoverageTypeToLabel(petitionerParty?.medical?.coverageTypeId);
    var respondentLineHText = convertMedicalCoverageTypeToLabel(respondentParty?.medical?.coverageTypeId);

    // if the OTHER party has titleXIX or hawkI, then the monthly medical cost is 0.0 for this party.
    if (petitionerParty?.medical?.coverageTypeId === 'titleXIX' || petitionerParty?.medical?.coverageTypeId === 'hawkI') {
        respondentLineH = 0.0;
        respondentLineHText = '';
    }
    if (respondentParty?.medical?.coverageTypeId === 'titleXIX' || respondentParty?.medical?.coverageTypeId === 'hawkI') {
        petitionerLineH = 0.0;
        petitionerLineHText = '';
    }

    let petitionerLineI = 0.0;
    let respondentLineI = 0.0;

    const healthInsuranceDifference = roundTo(Math.abs(petitionerLineH - respondentLineH), 2);

    if (petitionerLineH > respondentLineH) {
        // petitioner is paying more than respondent. they get a deduction.
        respondentLineI = roundTo(healthInsuranceDifference * respondentLineB / 100.0, 2);
    } else if (respondentLineH > petitionerLineH) {
        // respondent is paying more than the petitioner. they get a deduction.
        petitionerLineI = roundTo(healthInsuranceDifference * petitionerLineB / 100.0, 2);
    }


    // const childrenCount = petitioner.netMonthlyIncomeSection.children0to16 + petitioner.netMonthlyIncomeSection.children17Plus;
    // const petitionerCashMedicalSupportLevel = getCashMedicalSupportVersioned(caseData, petitionerLineA);
    // const petitionerCashMedicalSupportRate = petitionerCashMedicalSupportLevel?.rates[childrenCount - 1] ?? 0.0;
    // const petitionerCashMedicalSupportProvided = petitionerCashMedicalSupportLevel?.isCashMedicalSupport === true;

    const petitionerLineJ = roundTo(petitionerLineG + petitionerLineI, 2);
    const respondentLineJ = roundTo(respondentLineG + respondentLineI, 2);

    let petitionerLineK = 0.0;
    let respondentLineK = 0.0;

    if (petitionerLineJ > respondentLineJ) {
        petitionerLineK = roundTo(petitionerLineJ - respondentLineJ, 2);
    } else if (respondentLineJ > petitionerLineJ) {
        respondentLineK = roundTo(respondentLineJ - petitionerLineJ, 2);
    }


    const rv: Worksheet1Section4Model = {
        petitioner: {
            lineA: petitionerLineA,
            lineB: petitionerLineB,
            //lineC: combinedLineC,
            //lineD: petitionerLineD,
            lineE: petitionerLineE,
            lineF: petitionerLineF,
            lineG: petitionerLineG,
            lineH: petitionerLineH,
            lineHText: petitionerLineHText,
            lineI: petitionerLineI,
            lineJ: petitionerLineJ,
            lineK: petitionerLineK,
            finalChildSupportAmount: petitionerLineK,
            finalCashMedicalSupportAmount: 0.0,
        },
        respondent: {
            lineA: respondentLineA,
            lineB: respondentLineB,
            //lineC: combinedLineC,
            //lineD: petitionerLineD,
            lineE: respondentLineE,
            lineF: respondentLineF,
            lineG: respondentLineG,
            lineH: respondentLineH,
            lineHText: respondentLineHText,
            lineI: respondentLineI,
            lineJ: respondentLineJ,
            lineK: respondentLineK,
            finalChildSupportAmount: respondentLineK,
            finalCashMedicalSupportAmount: 0.0,
        },
        combined: {
            lineA: combinedLineA,
            lineB: combinedLineB,
            lineC: combinedLineC,
            lineD: combinedLineD
        }
    };

    return rv;
}

export function calcWorksheet1Section5(caseData: CaseData): Worksheet1Section5Model {
    const rv: Worksheet1Section5Model = {
    };

    return rv;
}

// export function calcWorksheet1Section6(caseData: CaseData): Worksheet1Section6Model {
//     const rv: Worksheet1Section6Model = {
//         section6A: {
//             numberOfChildren: 0.0,
//             ncpBasicSupportObligation: 0.0,
//             healthInsuranceAddOrDeduct: 0.0,
//             extraordinaryVisitationCredit: 0.0,
//             guidelineAmountOfChildSupport: 0.0,
//             guidelineAmountOfCashMedicalSupport: 0.0,
//         },
//         section6B: {
//             numberOfChildren: 0.0,
//             ncpBasicSupportObligation: 0.0,
//             healthInsuranceAddOrDeduct: 0.0,
//             extraordinaryVisitationCredit: 0.0,
//             guidelineAmountOfChildSupport: 0.0,
//             guidelineAmountOfCashMedicalSupport: 0.0,
//         }
//     };

//     return rv;
// }